'use client';

import React from 'react';
import * as HeroIcons from '@heroicons/react/24/outline';
import * as HeroIconsSolid from '@heroicons/react/24/solid';
import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';

type IconVariant = 'outline' | 'solid';

interface IconProps {
    name: string;
    variant?: IconVariant;
    size?: number;
    className?: string;
    onClick?: () => void;
    disabled?: boolean;
    [key: string]: any;
}

function Icon({
    name,
    variant = 'outline',
    size = 24,
    className = '',
    onClick,
    disabled = false,
    ...props
}: IconProps) {
    // Add safety checks for icon loading
    if (!name) {
        console.warn('Icon component: name prop is required');
        return (
            <QuestionMarkCircleIcon data-component-id="src/components/ui/AppIcon.tsx:33:12" data-component-path="src/components/ui/AppIcon.tsx" data-component-line="33" data-component-file="AppIcon.tsx" data-component-name="QuestionMarkCircleIcon" data-component-content="%7B%22elementName%22%3A%22QuestionMarkCircleIcon%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
                width={size}
                height={size}
                className={`text-gray-400 ${className}`}
                {...props}
            />
        );
    }

    try {
        const iconSet = variant === 'solid' ? HeroIconsSolid : HeroIcons;
        const IconComponent = iconSet[name as keyof typeof iconSet] as React.ComponentType<any>;

        if (!IconComponent) {
            console.warn(`Icon component: "${name}" not found in HeroIcons ${variant}`);
            return (
                <QuestionMarkCircleIcon data-component-id="src/components/ui/AppIcon.tsx:49:16" data-component-path="src/components/ui/AppIcon.tsx" data-component-line="49" data-component-file="AppIcon.tsx" data-component-name="QuestionMarkCircleIcon" data-component-content="%7B%22elementName%22%3A%22QuestionMarkCircleIcon%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
                    width={size}
                    height={size}
                    className={`text-gray-400 ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}
                    onClick={disabled ? undefined : onClick}
                    {...props}
                />
            );
        }

        return (
            <IconComponent data-component-id="src/components/ui/AppIcon.tsx:60:12" data-component-path="src/components/ui/AppIcon.tsx" data-component-line="60" data-component-file="AppIcon.tsx" data-component-name="IconComponent" data-component-content="%7B%22elementName%22%3A%22IconComponent%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
                width={size}
                height={size}
                className={`${disabled ? 'opacity-50 cursor-not-allowed' : onClick ? 'cursor-pointer hover:opacity-80' : ''} ${className}`}
                onClick={disabled ? undefined : onClick}
                {...props}
            />
        );
    } catch (error) {
        console.error('Error loading icon:', error);
        return (
            <QuestionMarkCircleIcon data-component-id="src/components/ui/AppIcon.tsx:71:12" data-component-path="src/components/ui/AppIcon.tsx" data-component-line="71" data-component-file="AppIcon.tsx" data-component-name="QuestionMarkCircleIcon" data-component-content="%7B%22elementName%22%3A%22QuestionMarkCircleIcon%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
                width={size}
                height={size}
                className={`text-gray-400 ${className}`}
                {...props}
            />
        );
    }
}

export default Icon;